import { ConstellationSchema } from "./constellationSchemaAlgo";

// Default height if nodeHeight is not provided (fallback)
const DEFAULT_NODE_HEIGHT = 200;

export function getConstellationPositions(
    schema: ConstellationSchema,
    centerX = 600,
    centerY = 300
  ) {
    const positions: { [key: string]: { x: number; y: number } } = {};
    const factCount = schema.factTables.length;
    const dimCount = schema.dimensionTables.length;
    const nodeHeights = schema.nodeHeights || {};

    /**
     * Get node height from pre-calculated nodeHeights map
     */
    const getNodeHeight = (name: string): number => {
      return nodeHeights[name] || DEFAULT_NODE_HEIGHT;
    };

    // Rectangle parameters - INCREASED for larger layout
    const minWidth = 1000;  // Increased from 400
    const width = Math.max(minWidth, 1000 * factCount); // Increased from 650
    const halfWidth = width / 2;
    
    // Calculate total height based on dimension tables' pre-calculated heights
    // We need to calculate heights for tables on vertical sides (left and right)
    const perSide = Math.ceil(dimCount / 4);
    
    // Calculate dynamic height based on the maximum cumulative heights on each side
    let rightSideHeight = 0;
    let leftSideHeight = 0;
    
    schema.dimensionTables.forEach((name: string, i: number) => {
      const side = Math.floor(i / perSide);
      const nodeHeight = getNodeHeight(name);
      
      if (side === 1) { // Right side
        rightSideHeight += nodeHeight;
      } else if (side === 3) { // Left side
        leftSideHeight += nodeHeight;
      }
    });
    
    // Use the maximum of calculated heights or minimum height
    const minHeight = 800;  // Increased from 400
    const dynamicHeight = Math.max(rightSideHeight, leftSideHeight, minHeight);
    const height = Math.max(dynamicHeight, 700 * Math.max(1, Math.ceil(dimCount / 4))); // Increased from 200
    const halfHeight = height / 2;

    const factSpacing = width / (factCount + 1); // spacing for fact tables
    const factY = centerY; // all facts on the horizontal center

    // Place fact tables in a horizontal line inside the rectangle
    schema.factTables.forEach((name: string, i: number) => {
      const x = centerX - halfWidth + factSpacing * (i + 1);
      positions[name] = {
        x,
        y: factY,
      };
    });

    // Place dimension tables with dynamic Y spacing based on pre-calculated node heights
    if (dimCount > 0) {
      // Calculate starting positions to CENTER nodes on each vertical side
      // We already calculated rightSideHeight and leftSideHeight above
      
      // For right side: center vertically around centerY
      // Start from (centerY - totalRightHeight/2) and go down
      let rightY = centerY - (rightSideHeight / 2);
      
      // For left side: center vertically around centerY  
      // Start from (centerY + totalLeftHeight/2) and go up
      let leftY = centerY + (leftSideHeight / 2);
      
      // Calculate spacing for horizontal sides
      const hSpacing = width/ (perSide+1);
      
      schema.dimensionTables.forEach((name: string, i: number) => {
        let x: number, y: number;
        const side = Math.floor(i / perSide);
        const posOnSide = i % perSide;
        const nodeHeight = getNodeHeight(name);
        
        switch (side) {
          case 0: // Top side (left to right)
            x = centerX - halfWidth + hSpacing * (posOnSide + 1);
            y = centerY - halfHeight;
            break;
          case 1: // Right side (top to bottom) - dynamic Y spacing
            x = centerX + halfWidth;
            y = rightY + nodeHeight / 2;
            rightY += nodeHeight; // Move down by this node's height
            break;
          case 2: // Bottom side (right to left)
            x = centerX + halfWidth - hSpacing * (posOnSide + 1);
            y = centerY + halfHeight;
            break;
          case 3: // Left side (bottom to top) - dynamic Y spacing
            x = centerX - halfWidth;
            y = leftY - nodeHeight / 2;
            leftY -= nodeHeight; // Move up by this node's height
            break;
          default:
            x = centerX;
            y = centerY;
        }
        positions[name] = { x, y };
      });
    }

    return positions;
  }
